import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  CalendarClock, Loader2, Play, Pause, XCircle, Clock, Gauge, Users,
} from "lucide-react";
import { API } from "@/lib/constants";

const DAYS = [
  { i: 0, label: "Mon" }, { i: 1, label: "Tue" }, { i: 2, label: "Wed" },
  { i: 3, label: "Thu" }, { i: 4, label: "Fri" }, { i: 5, label: "Sat" }, { i: 6, label: "Sun" },
];
const TZS = ["Africa/Lagos", "Africa/Nairobi", "Africa/Accra", "Africa/Johannesburg",
  "Europe/London", "America/New_York", "Asia/Dubai", "UTC"];

const fmt = (iso, tz) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: tz, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const REASON_LABEL = {
  outside_window: "will send once the window opens",
  daily_cap_rolled: "rolled to a later day (sender daily limit)",
  sender_pacing: "spaced out to keep ~5 min between this sender's emails",
  daily_cap: "sender daily limit reached",
  deferred_daily_cap: "pushed to a later day (sender daily limit hit today)",
  deferred_outside_window: "pushed to the next window (was outside sending hours)",
  deferred_sender_pacing: "spaced ~5 min from this sender's previous email",
  paused: "paused by you",
  greeting_held: "greeting unresolved (held)",
  waiting_for_slot: "waiting for its scheduled time",
};

export function SchedulePanel({ campaign, code, sendableCount, defaultDailyLimit, onConsumedCode }) {
  const [tz, setTz] = useState("Africa/Lagos");
  const [form, setForm] = useState({
    launch_local: "", days: [0, 1, 2, 3, 4], start: "08:00", end: "18:00",
    pacing_minutes: 5, batch_size: 10, batch_gap_minutes: 30, daily_limit: defaultDailyLimit || 50,
  });
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const tzOptions = TZS.includes(tz) ? TZS : [tz, ...TZS];

  useEffect(() => {
    if (defaultDailyLimit) setForm((f) => ({ ...f, daily_limit: defaultDailyLimit }));
  }, [defaultDailyLimit]);

  const req = useCallback(async (path, opts = {}) => {
    const r = await fetch(`${API}${path}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...opts,
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.detail || "Request failed");
    return d;
  }, []);

  const loadView = useCallback(async () => {
    if (!campaign?.campaign_id) return;
    try {
      const d = await req(`/campaigns/bespoke/${campaign.campaign_id}/schedule`);
      setView(d);
      const s = d?.schedule;
      if (s) {
        if (s.timezone) setTz(s.timezone);
        setForm((f) => ({
          ...f,
          days: s.window?.days || f.days,
          start: s.window?.start || f.start,
          end: s.window?.end || f.end,
          pacing_minutes: s.pacing_minutes ?? f.pacing_minutes,
          batch_size: s.batch_size ?? f.batch_size,
          batch_gap_minutes: s.batch_gap_minutes ?? f.batch_gap_minutes,
          daily_limit: s.daily_limit ?? f.daily_limit,
        }));
      }
    } catch { /* no schedule yet */ }
  }, [campaign, req]);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => { if (d.timezone) { setTz(d.timezone); } })
      .catch(() => {});
    loadView();
  }, [loadView]);

  const toggleDay = (i) =>
    setForm((f) => ({ ...f, days: f.days.includes(i) ? f.days.filter((d) => d !== i) : [...f.days, i].sort() }));

  const schedule = async () => {
    if (code.length !== 6) return toast.error("Enter the 6-digit code first");
    if (form.days.length === 0) return toast.error("Pick at least one sending day");
    setBusy(true);
    try {
      const d = await req(`/campaigns/bespoke/${campaign.campaign_id}/schedule`, {
        method: "POST",
        body: JSON.stringify({
          code,
          launch_local: form.launch_local || null,
          timezone: tz,
          window: { days: form.days, start: form.start, end: form.end },
          pacing_minutes: Number(form.pacing_minutes),
          batch_size: Number(form.batch_size),
          batch_gap_minutes: Number(form.batch_gap_minutes),
          daily_limit: Number(form.daily_limit),
        }),
      });
      toast.success(`Scheduled ${d.scheduled} email(s). Done by ${fmt(d.estimated_completion, tz)}. Nothing sent yet.`);
      setView(d.schedule_view);
      onConsumedCode && onConsumedCode();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const act = async (verb) => {
    setBusy(true);
    try {
      const d = await req(`/campaigns/bespoke/${campaign.campaign_id}/schedule/${verb}`, { method: "POST" });
      setView(d.schedule_view);
      toast.success(`Schedule ${d.schedule_status}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const status = view?.schedule_status;
  const isActive = status === "scheduled" || status === "paused";

  return (
    <div className="border rounded-lg p-4 space-y-4" data-testid="schedule-panel">
      <div className="flex items-center gap-2 text-slate-800 font-medium">
        <CalendarClock className="w-4 h-4 text-blue-600" /> Schedule &amp; throttle
        {status && (
          <Badge variant="outline" className={
            status === "scheduled" ? "bg-blue-50 text-blue-700 border-blue-200"
              : status === "paused" ? "bg-amber-50 text-amber-700 border-amber-200"
                : status === "cancelled" ? "bg-slate-100 text-slate-500 border-slate-200"
                  : "bg-slate-50 text-slate-600"} data-testid="schedule-status-badge">
            {status}
          </Badge>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <div className="space-y-1">
          <Label className="text-xs">Launch (leave blank = as soon as possible)</Label>
          <Input type="datetime-local" value={form.launch_local}
            onChange={(e) => setForm({ ...form, launch_local: e.target.value })}
            data-testid="schedule-launch" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Timezone</Label>
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger data-testid="schedule-tz"><SelectValue /></SelectTrigger>
            <SelectContent>{tzOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Sending days</Label>
        <div className="flex flex-wrap gap-1">
          {DAYS.map((d) => (
            <button key={d.i} type="button" onClick={() => toggleDay(d.i)}
              data-testid={`schedule-day-${d.i}`}
              className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                form.days.includes(d.i)
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        <div className="space-y-1">
          <Label className="text-xs">Window start</Label>
          <Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="schedule-start" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Window end</Label>
          <Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="schedule-end" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Pacing (min between emails)</Label>
          <Input type="number" min="1" value={form.pacing_minutes} onChange={(e) => setForm({ ...form, pacing_minutes: e.target.value })} data-testid="schedule-pacing" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Batch size</Label>
          <Input type="number" min="1" value={form.batch_size} onChange={(e) => setForm({ ...form, batch_size: e.target.value })} data-testid="schedule-batch-size" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Gap between batches (min)</Label>
          <Input type="number" min="1" value={form.batch_gap_minutes} onChange={(e) => setForm({ ...form, batch_gap_minutes: e.target.value })} data-testid="schedule-batch-gap" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Sender daily limit</Label>
          <Input type="number" min="1" value={form.daily_limit} onChange={(e) => setForm({ ...form, daily_limit: e.target.value })} data-testid="schedule-daily-limit" />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={schedule} disabled={busy || code.length !== 6 || sendableCount === 0}
          className="bg-blue-600 hover:bg-blue-700" data-testid="schedule-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-2" />}
          Schedule send
        </Button>
        <span className="text-xs text-slate-500">Uses the 6-digit code above — authorise once; scheduled sends need no further approval.</span>
      </div>

      {view && isActive && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2 text-sm" data-testid="schedule-view">
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <span className="flex items-center gap-1 text-slate-600"><Clock className="w-3.5 h-3.5" /> Next: <strong>{fmt(view.next_send_at, view.schedule?.timezone || tz)}</strong></span>
            <span className="flex items-center gap-1 text-slate-600"><CalendarClock className="w-3.5 h-3.5" /> Est. done: <strong data-testid="schedule-eta">{fmt(view.estimated_completion, view.schedule?.timezone || tz)}</strong></span>
            <span className="flex items-center gap-1 text-slate-600"><Users className="w-3.5 h-3.5" /> Queued: <strong>{view.queued}</strong>{view.held ? ` · held ${view.held}` : ""}</span>
            {view.sender_usage && (
              <span className="flex items-center gap-1 text-slate-600"><Gauge className="w-3.5 h-3.5" /> Today: <strong>{view.sender_usage.sent_today}/{view.sender_usage.daily_limit}</strong></span>
            )}
          </div>
          {view.delay_reasons && Object.keys(view.delay_reasons).length > 0 && (
            <div className="text-xs text-slate-500" data-testid="schedule-reasons">
              Queue delays: {Object.entries(view.delay_reasons).map(([k, v]) => `${v} ${REASON_LABEL[k] || k}`).join(" · ")}
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            {status === "scheduled" && (
              <Button size="sm" variant="outline" onClick={() => act("pause")} disabled={busy} data-testid="schedule-pause-btn">
                <Pause className="w-3.5 h-3.5 mr-1" /> Pause
              </Button>
            )}
            {status === "paused" && (
              <Button size="sm" variant="outline" onClick={() => act("resume")} disabled={busy} data-testid="schedule-resume-btn">
                <Play className="w-3.5 h-3.5 mr-1" /> Resume
              </Button>
            )}
            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => act("cancel")} disabled={busy} data-testid="schedule-cancel-btn">
              <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel remaining
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
